import Autocomplete from '@mui/material/Autocomplete';
import Checkbox from '@mui/material/Checkbox';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Dialog, DialogActionsWithButtons, DialogContent } from '@side/pantry';
import { useCallback, useRef, useState } from 'react';
import { DropdownLoadingIcon } from 'src/components/DropdownLoadingIcon';
import {
  TrackHomePageEvent,
  useTrackHomePageAnalytics,
} from '$analytics/useTrackAnalytics/domains/homePage';
import type { SubmitAsOption } from '../types';
import { useFactsAndFiguresContext } from './FactsAndFiguresContext';
import { MobileDatesFilterOptions } from './MobileDatesFilterOptions';

export function TransactionStatusSnapshotModal() {
  const [agentSearch, setAgentSearch] = useState('');
  const hasChangedSearchOnce = useRef(false);

  const {
    agentOptions,
    agents,
    dateRange,
    endDate,
    filtersCount,
    isLoadingAgents,
    isModalOpen,
    onChangeAgents,
    onChangeDateRange,
    onChangeEndDate,
    onChangeStartDate,
    onClearAgents,
    onReset,
    startDate,
    toggleModal,
  } = useFactsAndFiguresContext();
  const trackEvent = useTrackHomePageAnalytics();

  const closeModal = useCallback(() => {
    hasChangedSearchOnce.current = false;
    toggleModal();
  }, [toggleModal]);

  const onSubmit = useCallback(() => {
    trackEvent(TrackHomePageEvent.FactsAndFiguresMobileFilterDoneClick, {
      dateType: null,
      startDate: null,
      endDate: null,
      agentId: agents.map((a) => a.value).join(','),
    });
    closeModal();
  }, [agents, closeModal, trackEvent]);

  return (
    <Dialog maxWidth="sm" onClose={closeModal} open={isModalOpen}>
      <DialogContent onClose={closeModal} title="Filters">
        <Typography sx={{ marginBottom: 1 }} variant="subtitle1">
          Transaction Created
        </Typography>

        <MobileDatesFilterOptions
          selectedDateRange={dateRange}
          endDate={endDate}
          onChangeDateRange={onChangeDateRange}
          onChangeEndDate={onChangeEndDate}
          onChangeStartDate={onChangeStartDate}
          startDate={startDate}
        />

        <Typography sx={{ marginBottom: 1 }} variant="subtitle1">
          Agents
        </Typography>

        <Autocomplete
          autoComplete
          disableCloseOnSelect
          disablePortal
          disabled={isLoadingAgents}
          inputValue={agentSearch}
          slotProps={{
            listbox: {
              sx: {
                '& .MuiAutocomplete-option': {
                  fontSize: 14,
                },
              },
            },
          }}
          multiple
          onChange={(_, newAgents, reason) => {
            if (reason === 'clear') {
              onClearAgents();
            }

            if (reason === 'removeOption') {
              trackEvent(
                TrackHomePageEvent.FactsAndFiguresAgentFilterDeselectClick,
                {
                  agentId: (newAgents as SubmitAsOption[])
                    ?.map((a) => a.value)
                    .join(','),
                },
              );
            }

            if (reason === 'selectOption') {
              trackEvent(
                TrackHomePageEvent.FactsAndFiguresAgentFilterSelectClick,
                {
                  agentId: (newAgents as SubmitAsOption[])
                    ?.map((a) => a.value)
                    .join(','),
                },
              );
            }

            if (
              ['removeOption', 'selectOption'].includes(reason) &&
              Array.isArray(newAgents)
            ) {
              onChangeAgents(newAgents as SubmitAsOption[]);
            }
          }}
          onInputChange={(_, value) => {
            hasChangedSearchOnce.current = true;

            setAgentSearch(value);
          }}
          options={agentOptions}
          popupIcon={isLoadingAgents ? <DropdownLoadingIcon /> : undefined}
          renderInput={(params) => (
            <TextField
              {...params}
              label={agents.length ? '' : 'Search or select'}
              onBlur={() => {
                if (hasChangedSearchOnce.current) {
                  trackEvent(
                    TrackHomePageEvent.FactsAndFiguresAgentSearchUpdate,
                    {
                      searchValue: agentSearch,
                    },
                  );
                }
              }}
              sx={{
                '&.MuiAutocomplete-root.MuiAutocomplete-input': {
                  py: '0px !important',
                },
                '& .MuiInputLabel-root': {
                  fontSize: '16px !important',
                  height: '22px !important',
                  transform: 'translate(14px, 12px) scale(1)',
                  '&.Mui-focused': {
                    display: 'none',
                  },
                },
                '& .MuiInputBase-root': {
                  '& .MuiInputBase-input': {
                    height: agents.length ? 0 : 22,
                    py: '0px !important',
                    pl: '4px !important',
                    '&[aria-expanded="true"]': {
                      height: 22,
                    },
                  },
                },
                '& .MuiAutocomplete-inputRoot': {
                  py: '10px !important',
                  pl: '12px !important',
                },
                '& .MuiChip-root': {
                  mt: '2px !important',
                },
              }}
              slotProps={{
                ...params.slotProps,
                inputLabel: {
                  ...params.slotProps.inputLabel,
                  shrink: false,
                  sx: {
                    display: agentSearch.length ? 'none' : 'unset',
                    fontSize: 16,
                    height: 22,
                    padding: 0,
                    transform: 'translate(14px, 10px) scale(1) !important',
                    '&.Mui-focused': {
                      display: 'none',
                    },
                  },
                },
              }}
            />
          )}
          renderOption={(optionProps, { label }, { selected }) => {
            const [displayName, ...teamNameSplit] = label.split('-');
            const teamName = teamNameSplit.join('-');

            return (
              <Stack
                {...optionProps}
                component="li"
                sx={{
                  alignItems: 'center',
                  flexDirection: 'row',
                  height: 42,
                  marginY: 0.25,
                  paddingLeft: '0 !important',
                  '&.MuiAutocomplete-option[aria-selected="true"]': {
                    backgroundColor: 'white !important',
                  },
                  '&.MuiAutocomplete-option[aria-selected="true"]:hover': {
                    backgroundColor: (t) =>
                      `${t.palette.action.hover} !important`,
                  },
                }}
              >
                <Checkbox checked={selected} />

                <Typography sx={{ marginRight: 2 }} variant="body2">
                  {displayName}
                </Typography>

                <Typography sx={{ color: 'text.secondary' }} variant="body3">
                  {teamName}
                </Typography>
              </Stack>
            );
          }}
          value={agents}
        />
      </DialogContent>

      <DialogActionsWithButtons
        CancelButtonProps={{
          buttonText: 'Clear All',
          disabled: filtersCount === 0,
          onClick: onReset,
        }}
        SubmitButtonProps={{
          defaultText: 'Done',
          onClick: onSubmit,
        }}
      />
    </Dialog>
  );
}
